/**
 * Galactic chart overlay: nearby galaxies on the lattice around the current
 * one, selectable with the mouse. Selection sets a nav heading (the ship
 * turns to face it) and arms the hyperlight charge (hold Space).
 *
 * Some destinations are flagged as DYING — flavor for the Haven / Dying
 * Galaxies design (see docs/design/dying-galaxies.md); the lifecycle
 * mechanics arrive in a later phase, the chart language arrives now.
 */
import { generateSystem, parseSystemId, parseSystemRef, sealedSystemStub, systemDefFor, systemIdAt } from "@hyperlight/shared";

export interface NavTarget {
  sx: number;
  sy: number;
  sz: number;
  name: string;
  /** Full id when the target is private (`home:<uid>`) or sealed
   *  (`sealed:<x>:<y>:<z>`). Home coordinates are placeholders; sealed
   *  coordinates remain meaningful for routing. */
  id?: string;
}

/** The wire/system id of a nav target — lattice triple unless `id` overrides. */
export function navTargetId(t: NavTarget): string {
  return t.id ?? `${t.sx}:${t.sy}:${t.sz}`;
}


import { SensorView, type SensorNode } from "./sensorView";
import { strand } from "./meridian";

/** Who the chart should mark besides stars: friends, party members, and the
 *  party's rally flag. Systems are wire ids ("x:y:z"); the chart resolves the
 *  lattice offset itself and folds each mark into the star it belongs to. */
export interface ChartPresence {
  system: string;
  label: string;
  kind: "friend" | "party" | "rally";
}

export class NavChart {
  private el: HTMLElement;
  visible = false;
  private sysTimer: number | null = null;
  /** Pending fade-out timer (close leaves the overlay up while opacity animates). */
  private fadeTimer: number | null = null;
  private sysView = new SensorView();
  private galView = new SensorView();

  constructor(private handlers: { onSelect: (t: NavTarget) => void; onClose: () => void }) {
    this.el = document.createElement("div");
    this.el.id = "nav-chart";
    this.el.style.display = "none";
    document.body.appendChild(this.el);
  }

  /** Build and show the chart centered on the current galaxy.
   *  C4: `coords` is null inside a home system — the galaxy bubble then shows
   *  only the private pocket (leaving goes through the homeJump button). */
  open(
    coords: [number, number, number] | null,
    discovery?: {
      /** Charted system ids ("x:y:z"). */
      known: Set<string>;
      /** The home base id — always charted, marked on the map. */
      base: string;
      /** The pilot's chosen home WORLD, resolved to a display name. */
      homeWorldName: string | null;
      /** Nearest world right now (candidate for SET HOME), or null. */
      nearestWorld: string | null;
      onSetHome: () => void;
    },
    /** Sensor-bubble views: live system nodes + the 3D neighbor lattice. */
    view?: {
      systemName: string;
      systemNodes: () => SensorNode[];
      neighbors: { id: string; sx: number; sy: number; sz: number; name: string; dist: number }[];
      /** Click a system body → set/replace the flight waypoint. */
      onWaypoint: (id: string, name: string) => void;
    },
    /** On foot only: a button to recall the ship into the map. */
    recall?: {
      canRecall: boolean;
      onRecall: () => void;
    },
    /** C4 home-system travel: JUMP HOME (in lattice space, authed) or
     *  RETURN — <system> (inside the home pocket). Null hides the button. */
    homeJump?: { label: string; target: NavTarget } | null,
    /** Social marks on the galaxy map (friends / party / rally flag). */
    presence?: () => ChartPresence[],
  ): void {
    const [cx, cy, cz] = coords ?? [0, 0, 0];
    this.visible = true;
    if (this.fadeTimer !== null) {
      clearTimeout(this.fadeTimer);
      this.fadeTimer = null;
    }
    this.el.style.display = "flex";
    void this.el.offsetWidth; // commit display before the fade starts
    this.el.classList.add("open");
    this.el.innerHTML = `
      <div class="nc-full">
        <div class="nc-bar">
          <div class="nc-header">GALACTIC CHART</div>
          <div class="nc-tabs">
            <button class="nc-tab" data-tab="galaxy">GALAXY</button>
            <button class="nc-tab nc-tab-on" data-tab="system">SYSTEM</button>
          </div>
          <div class="nc-sub" id="nc-hint"></div>
          <div class="nc-home">
            ⌂ <b>${discovery?.homeWorldName ?? "no home set"}</b>
            ${discovery?.nearestWorld ? `<button id="nc-sethome">SET ${discovery.nearestWorld.toUpperCase()}</button>` : ""}
          </div>
          ${homeJump ? `<button class="nc-recall" id="nc-homejump">${homeJump.label}</button>` : ""}
          ${recall?.canRecall ? `<button class="nc-recall" id="nc-recall">⮌ RECALL SHIP</button>` : ""}
          <button class="nc-close" id="nc-close">CLOSE (esc)</button>
        </div>
        <canvas id="nc-galmap" style="display:none"></canvas>
        <canvas id="nc-sysmap"></canvas>
      </div>
    `;
    // Fullscreen canvases; SYSTEM opens first — the player "zooms out" of
    // where they are, and one more tab-click zooms out to the galaxy.
    const sysCanvas = this.el.querySelector<HTMLCanvasElement>("#nc-sysmap")!;
    const galCanvas = this.el.querySelector<HTMLCanvasElement>("#nc-galmap")!;
    const hintEl = this.el.querySelector<HTMLElement>("#nc-hint")!;
    const bar = this.el.querySelector<HTMLElement>(".nc-bar")!;
    const sizeCanvas = (c: HTMLCanvasElement): void => {
      c.width = window.innerWidth;
      c.height = window.innerHeight - bar.offsetHeight;
    };
    sizeCanvas(sysCanvas);
    sizeCanvas(galCanvas);
    const HINTS = {
      system: `${view ? view.systemName.toUpperCase() : "SYSTEM"} — drag to rotate · scroll to zoom · click a body to set a waypoint`,
      galaxy: "drag to rotate · scroll to zoom · click a star to set course",
    };
    hintEl.textContent = HINTS.system;
    const redraw = (): void => {
      if (!view) return;
      const ctx = sysCanvas.getContext("2d");
      if (ctx) {
        this.sysView.draw(ctx, sysCanvas.width, sysCanvas.height, view.systemNodes(), {
          labels: true,
          sqrtScale: true,
          gridRings: 4,
        });
      }
    };
    // Intro: first draw establishes the projector, then ease out from the
    // player's spot. rAF drives the animation for either view.
    const runIntro = (
      v: SensorView,
      drawFn: () => void,
      focus: [number, number, number],
    ): void => {
      drawFn();
      const off = v.lastProjRaw ? v.lastProjRaw(...focus) : [0, 0];
      v.introZoomOut(off[0], off[1], 4.5);
      let last = performance.now();
      const step = (): void => {
        const now = performance.now();
        const dt = Math.min(0.05, (now - last) / 1000);
        last = now;
        if (v.tickIntro(dt) && this.visible) {
          drawFn();
          requestAnimationFrame(step);
        } else {
          drawFn();
        }
      };
      requestAnimationFrame(step);
    };
    // System map: click = waypoint.
    let sysDrag = 0;
    sysCanvas.addEventListener("pointerdown", () => (sysDrag = 0));
    sysCanvas.addEventListener("pointermove", (e) => (sysDrag += Math.abs(e.movementX) + Math.abs(e.movementY)));
    sysCanvas.addEventListener("pointerup", (e) => {
      if (sysDrag > 6 || !view) return;
      const hitId = this.sysView.hit(e.offsetX, e.offsetY);
      if (!hitId || !hitId.includes("|")) return;
      const [id, name] = hitId.split("|");
      view.onWaypoint(id, name);
      redraw();
    });

    // GALAXY bubble: the full 3D neighbor lattice (the old flat grid hid
    // every system above and below this plane). Click a star to set course.
    const galNodes = (): SensorNode[] => {
      const out: SensorNode[] = [];
      if (coords === null) {
        // Inside a home system: a private pocket off the lattice — no charted
        // stars to plot relative to nowhere. RETURN is the way out.
        out.push({ x: 0, y: 0, z: 0, color: "#ffd86a", size: 5, shape: "ringdot", label: "HOME SYSTEM — off the lattice" });
        return out;
      }
      out.push({ x: 0, y: 0, z: 0, color: "#7fd8ff", size: 4.5, shape: "ringdot", label: "YOU ARE HERE" });
      // Charted systems BEYOND the neighbor shell (the fast-travel list's
      // replacement): warp anywhere you've been, straight off the map.
      for (const id of discovery?.known ?? []) {
        const ref = parseSystemRef(id);
        // Home pockets and cache instances (non-lattice instance ids — the
        // cache renderer lands in Phase 4) have no coords to plot.
        if (!ref || ref.kind === "home" || ref.kind === "cache") continue;
        const [kx, ky, kz] = ref.c;
        if (Math.abs(kx - cx) <= 3 && Math.abs(ky - cy) <= 3 && Math.abs(kz - cz) <= 3) continue;
        const def = ref.kind === "sealed"
          ? (systemDefFor(id) ?? sealedSystemStub(kx, ky, kz))
          : generateSystem(kx, ky, kz);
        out.push({
          x: kx - cx,
          y: ky - cy,
          z: kz - cz,
          color: id === discovery?.base ? "#ffd86a" : "#5ec46a",
          size: 3.2,
          label: def.name,
          sub: `${Math.hypot(kx - cx, ky - cy, kz - cz).toFixed(1)} ly`,
          id,
        });
      }
      // Social marks fold INTO the star they belong to (two icons on one
      // coordinate would just fight); a system off the charted list gets a
      // star of its own so a friend is never invisible.
      const marks = new Map<string, { label: string; kind: ChartPresence["kind"] }>();
      for (const m of presence?.() ?? []) {
        const prev = marks.get(m.system);
        // Rally outranks party outranks friend for the colour it lends.
        const rank = { rally: 2, party: 1, friend: 0 } as const;
        if (!prev) marks.set(m.system, { label: m.label, kind: m.kind });
        else {
          marks.set(m.system, {
            label: `${prev.label}, ${m.label}`,
            kind: rank[m.kind] > rank[prev.kind] ? m.kind : prev.kind,
          });
        }
      }
      for (const n of view?.neighbors ?? []) {
        const id = n.id;
        const known = discovery?.known.has(id) ?? false;
        const isBase = discovery?.base === id;
        out.push({
          x: n.sx - cx,
          y: n.sy - cy,
          z: n.sz - cz,
          color: isBase ? "#ffd86a" : known ? "#5ec46a" : "#c8d6e4",
          size: known || isBase ? 3.4 : 2.4,
          dim: !known && !isBase && n.dist > 1.8,
          label: known || isBase || n.dist <= 1.75 ? n.name : undefined,
          sub: known || isBase ? undefined : `${n.dist.toFixed(1)} ly`,
          id,
        });
      }
      for (const [id, mark] of marks) {
        const ref = parseSystemRef(id);
        // Private pockets aren't on the lattice, and neither is a cache
        // instance id (non-lattice instance; Phase 4 renderer) — skip both.
        if (!ref || ref.kind === "home" || ref.kind === "cache") continue;
        const [mx, my, mz] = ref.c;
        const color = strand(mark.kind === "friend" ? "friends" : "party");
        const hit = out.find((n) => n.id === id);
        if (hit) {
          hit.color = color;
          hit.size = Math.max(hit.size, 4);
          hit.dim = false;
          hit.sub = mark.label;
          hit.ring = mark.kind === "rally" || hit.ring;
        } else if (mx === cx && my === cy && mz === cz) {
          // Right here with you — the world markers already say so.
          continue;
        } else {
          out.push({
            x: mx - cx, y: my - cy, z: mz - cz,
            color, size: 4, shape: "ringdot",
            label: systemDefFor(id)?.name ?? id,
            sub: mark.label,
            id,
          });
        }
      }
      return out;
    };
    const galRedraw = (): void => {
      const ctx = galCanvas.getContext("2d");
      if (ctx) {
        this.galView.draw(ctx, galCanvas.width, galCanvas.height, galNodes(), {
          labels: true,
          sqrtScale: false,
          gridRings: 3,
        });
      }
    };
    this.galView.attach(galCanvas, galRedraw);
    this.sysView.attach(sysCanvas, redraw);
    // Open ON the system view, zooming out from the player.
    if (view) {
      const selfNode = view.systemNodes().find((n) => n.shape === "arrow");
      runIntro(this.sysView, redraw, selfNode ? [selfNode.x, selfNode.y, selfNode.z] : [0, 0, 0]);
      if (this.sysTimer !== null) clearInterval(this.sysTimer);
      this.sysTimer = window.setInterval(redraw, 400);
    }
    let galDragDist = 0;
    let galDownAt: [number, number] | null = null;
    galCanvas.addEventListener("pointerdown", (e) => {
      galDownAt = [e.offsetX, e.offsetY];
      galDragDist = 0;
    });
    galCanvas.addEventListener("pointermove", (e) => {
      if (galDownAt) galDragDist += Math.abs(e.movementX) + Math.abs(e.movementY);
    });
    galCanvas.addEventListener("pointerup", (e) => {
      // A drag is a rotate, not a pick.
      if (galDragDist > 6 || !galDownAt) return;
      const hitId = this.galView.hit(e.offsetX, e.offsetY);
      galDownAt = null;
      if (!hitId) return;
      const ref = parseSystemRef(hitId);
      // Nothing to course-set toward without lattice coords: home pockets and
      // cache instance ids (non-lattice; Phase 4 renderer) aren't pickable.
      if (!ref || ref.kind === "home" || ref.kind === "cache") return;
      const m = ref.c;
      // Neighbors carry their names; far charted systems re-derive them.
      const n = view?.neighbors.find((x) => x.sx === m[0] && x.sy === m[1] && x.sz === m[2]);
      const targetId = n?.id ?? systemIdAt(m[0], m[1], m[2]);
      const name = n?.name ?? (parseSystemId(targetId)
        ? generateSystem(m[0], m[1], m[2]).name
        : (systemDefFor(targetId) ?? sealedSystemStub(m[0], m[1], m[2])).name);
      this.close();
      this.handlers.onSelect({ sx: m[0], sy: m[1], sz: m[2], name, ...(targetId.startsWith("sealed:") ? { id: targetId } : {}) });
    });
    for (const tab of this.el.querySelectorAll<HTMLElement>(".nc-tab")) {
      tab.addEventListener("click", () => {
        const sysOn = tab.dataset.tab === "system";
        galCanvas.style.display = sysOn ? "none" : "";
        sysCanvas.style.display = sysOn ? "" : "none";
        hintEl.textContent = sysOn ? HINTS.system : HINTS.galaxy;
        for (const t of this.el.querySelectorAll(".nc-tab")) t.classList.toggle("nc-tab-on", t === tab);
        if (this.sysTimer !== null) {
          clearInterval(this.sysTimer);
          this.sysTimer = null;
        }
        if (sysOn) {
          redraw();
          this.sysTimer = window.setInterval(redraw, 400); // live orbits
        } else {
          // Galaxy entry: zoom out from "you are here" (the origin).
          runIntro(this.galView, galRedraw, [0, 0, 0]);
        }
      });
    }
    this.el.querySelector("#nc-sethome")?.addEventListener("click", () => {
      discovery?.onSetHome();
      this.close();
      this.handlers.onClose();
    });
    this.el.querySelector("#nc-homejump")?.addEventListener("click", () => {
      if (!homeJump) return;
      this.close();
      this.handlers.onSelect(homeJump.target);
    });
    this.el.querySelector("#nc-recall")?.addEventListener("click", () => {
      recall?.onRecall();
      this.close();
      this.handlers.onClose();
    });
    this.el.querySelector("#nc-close")!.addEventListener("click", () => {
      this.close();
      this.handlers.onClose();
    });
  }

  close(): void {
    this.visible = false;
    // Fade out, then actually leave the DOM flow. Reopening cancels the timer.
    this.el.classList.remove("open");
    this.fadeTimer ??= window.setTimeout(() => {
      this.el.style.display = "none";
      this.fadeTimer = null;
    }, 260);
    if (this.sysTimer !== null) {
      clearInterval(this.sysTimer);
      this.sysTimer = null;
    }
  }
}
